import { Component, OnDestroy, OnInit } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FormControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { emailFormatValidator } from '@app/utilities/device-alert';
import { WebClient, User$ } from '@injectables';
import { Site } from '@models';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { FragQueryService } from 'src/injectables/frag-query.service';

@Component({
    selector: 'vehicle-idle-alert',
    templateUrl: './vehicle-idle-alert.component.html',
    styleUrls: ['./vehicle-idle-alert.component.scss'],
})
export class VehicleIdleAlertComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    public selectedSite: Site | null = null;
    public readonly emailFormControl = new FormControl('', [emailFormatValidator]);
    public alertEmails: string[] = [];
    public readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    public readonly searchControl = new FormControl('');
    public readonly sites$: Observable<Site[]> = this.fragQuery.sites$;
    public readonly showSiteDropdown$ = this.user$.pipe(
        map(user => user?.permissions >= 2),
    );
    public filteredSites$: Observable<Site[]>;

    constructor(
        private readonly webClient: WebClient,
        private readonly fragQuery: FragQueryService,
        private readonly user$: User$,
        private readonly dialog: DialogService,
        private readonly snackBar: MatSnackBar,
    ) {}

    public ngOnInit(): void {
        this.filteredSites$ = combineLatest([
            this.sites$,
            this.searchControl.valueChanges.pipe(startWith('')),
        ]).pipe(
            map(([sites, search]) => this.filterSites(sites, search)),
        );

        combineLatest([this.sites$, this.fragQuery.siteId$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([sites, selectedSiteId]) => {
                const siteId = this.selectedSite?.id ?? selectedSiteId;
                const site = sites.find(value => value.id === siteId) ?? sites[0] ?? null;
                this.selectSite(site);
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public selectSite(site: Site | null): void {
        this.selectedSite = site;
        this.alertEmails = (site?.vehicleAlert ?? '')
            .split(',')
            .map(email => email.trim())
            .filter(email => email.length > 0);
        this.emailFormControl.reset();
    }

    public onSiteDropdownClosed(): void {
        this.searchControl.setValue('');
    }

    private filterSites(sites: Site[], search: string): Site[] {
        const value = (search ?? '').toLowerCase();
        return sites.filter(site => site.name.toLowerCase().includes(value));
    }

    public addEmail(event: MatChipInputEvent): void {
        const email = (event.value ?? '').trim();
        if (!email) return;

        this.emailFormControl.setValue(email);
        this.emailFormControl.markAsDirty();
        if (this.emailFormControl.valid && !this.alertEmails.includes(email)) {
            this.alertEmails.push(email);
            event.chipInput?.clear();
            this.emailFormControl.reset();
            return;
        }

        this.snackBar.open('Invalid email format', 'OK', {
            duration: 3000,
            panelClass: ['snackbar-success'],
        });
    }

    public removeEmail(email: string): void {
        this.alertEmails = this.alertEmails.filter(value => value !== email);
    }

    public valid(): boolean {
        const pendingEmail = this.emailFormControl.value?.trim();
        if (!this.selectedSite) return false;
        if (pendingEmail) return this.emailFormControl.valid;
        return this.alertEmails.length > 0;
    }

    public async saveSettings(): Promise<void> {
        if (!this.valid()) {
            this.snackBar.open('Select a site and add at least one valid email address.', 'OK', {
                duration: 3000,
                panelClass: ['snackbar-success'],
            });
            return;
        }

        const saved = await this.dialog.await(this.webClient.model.site.put({
            where: this.selectedSite.id,
            fields: { vehicleAlert: this.alertEmails.join(',') },
        }));

        if (!saved) return;

        this.selectedSite.vehicleAlert = this.alertEmails.join(',');
        this.fragQuery.trigger$.next();
        this.snackBar.open('Vehicle idle alert settings saved.', 'OK', {
            duration: 3000,
            panelClass: ['snackbar-success'],
        });
    }
}
